import { CheckCircle2, CircleAlert, Info, type LucideIcon, TriangleAlert, X } from 'lucide-react'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { cn } from '@/lib/utils'

type NotificationVariant = 'success' | 'info' | 'warning' | 'error'

type NotificationInput = {
  title: string
  description: string
  variant?: NotificationVariant
  durationMs?: number
}

type Notification = Required<Pick<NotificationInput, 'title' | 'variant' | 'durationMs'>> &
  Pick<NotificationInput, 'description'> & {
    id: string
  }

type NotificationsContextValue = {
  notify: (notification: NotificationInput) => string
  dismiss: (id: string) => void
}

const DEFAULT_DURATION_MS = 5000
const NOTIFICATION_LIMIT = 4
const NOTIFICATION_SOUND_COOLDOWN_MS = 900

const NotificationsContext = createContext<NotificationsContextValue | null>(null)

const variantStyles: Record<
  NotificationVariant,
  {
    icon: LucideIcon
    accentClassName: string
    containerClassName: string
    iconClassName: string
  }
> = {
  success: {
    icon: CheckCircle2,
    accentClassName: 'border-l-primary',
    containerClassName: 'border-primary bg-popover shadow-primary/20 ring-primary/20',
    iconClassName: 'bg-primary text-primary-foreground',
  },
  info: {
    icon: Info,
    accentClassName: 'border-l-muted-foreground',
    containerClassName: 'border-border bg-popover shadow-foreground/10 ring-border',
    iconClassName: 'bg-muted text-foreground',
  },
  warning: {
    icon: TriangleAlert,
    accentClassName: 'border-l-accent-foreground',
    containerClassName:
      'border-accent-foreground bg-popover shadow-accent-foreground/15 ring-accent-foreground/20',
    iconClassName: 'bg-accent text-accent-foreground',
  },
  error: {
    icon: CircleAlert,
    accentClassName: 'border-l-destructive',
    containerClassName: 'border-destructive bg-popover shadow-destructive/15 ring-destructive/20',
    iconClassName: 'bg-destructive text-white',
  },
}

let notificationSequence = 0
let notificationAudioContext: AudioContext | null = null
let lastNotificationSoundAt = 0

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const timeoutsRef = useRef(new Map<string, number>())

  const dismiss = useCallback((id: string) => {
    const timeoutId = timeoutsRef.current.get(id)

    if (timeoutId) {
      window.clearTimeout(timeoutId)
      timeoutsRef.current.delete(id)
    }

    setNotifications((current) => current.filter((notification) => notification.id !== id))
  }, [])

  const notify = useCallback(
    ({
      title,
      description,
      variant = 'success',
      durationMs = DEFAULT_DURATION_MS,
    }: NotificationInput) => {
      notificationSequence += 1

      const id = `notification-${Date.now()}-${notificationSequence}`
      const notification: Notification = { id, title, description, variant, durationMs }

      setNotifications((current) => [notification, ...current].slice(0, NOTIFICATION_LIMIT))
      playNotificationSound()

      if (durationMs > 0) {
        const timeoutId = window.setTimeout(() => dismiss(id), durationMs)
        timeoutsRef.current.set(id, timeoutId)
      }

      return id
    },
    [dismiss],
  )

  const contextValue = useMemo(() => ({ notify, dismiss }), [notify, dismiss])

  useEffect(() => {
    const unlockAudio = () => unlockNotificationSound()

    window.addEventListener('pointerdown', unlockAudio, { once: true, passive: true })
    window.addEventListener('keydown', unlockAudio, { once: true })

    return () => {
      window.removeEventListener('pointerdown', unlockAudio)
      window.removeEventListener('keydown', unlockAudio)

      for (const timeoutId of timeoutsRef.current.values()) {
        window.clearTimeout(timeoutId)
      }

      timeoutsRef.current.clear()
    }
  }, [])

  return (
    <NotificationsContext.Provider value={contextValue}>
      {children}
      <NotificationViewport notifications={notifications} onDismiss={dismiss} />
    </NotificationsContext.Provider>
  )
}

function getNotificationAudioContext() {
  if (typeof window === 'undefined') {
    return null
  }

  const AudioContextConstructor =
    window.AudioContext ??
    (window as Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext

  if (!AudioContextConstructor) {
    return null
  }

  notificationAudioContext ??= new AudioContextConstructor()

  return notificationAudioContext
}

function unlockNotificationSound() {
  const audioContext = getNotificationAudioContext()

  if (audioContext?.state !== 'suspended') {
    return
  }

  void audioContext.resume().catch(() => undefined)
}

function playNotificationSound() {
  const now = Date.now()

  if (now - lastNotificationSoundAt < NOTIFICATION_SOUND_COOLDOWN_MS) {
    return
  }

  const audioContext = getNotificationAudioContext()

  if (!audioContext) {
    return
  }

  lastNotificationSoundAt = now

  if (audioContext.state === 'suspended') {
    void audioContext
      .resume()
      .then(() => {
        if (audioContext.state === 'running') {
          playNotificationChime(audioContext)
        }
      })
      .catch(() => undefined)
    return
  }

  playNotificationChime(audioContext)
}

function playNotificationChime(audioContext: AudioContext) {
  const startAt = audioContext.currentTime
  const output = audioContext.createGain()

  output.gain.setValueAtTime(0.0001, startAt)
  output.gain.exponentialRampToValueAtTime(0.08, startAt + 0.02)
  output.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.44)
  output.connect(audioContext.destination)

  playTone(audioContext, output, 659.25, 0, 0.18)
  playTone(audioContext, output, 880, 0.11, 0.26)

  window.setTimeout(() => output.disconnect(), 600)
}

function playTone(
  audioContext: AudioContext,
  destination: AudioNode,
  frequency: number,
  delay: number,
  duration: number,
) {
  const oscillator = audioContext.createOscillator()
  const gain = audioContext.createGain()
  const startAt = audioContext.currentTime + delay
  const stopAt = startAt + duration

  oscillator.type = 'sine'
  oscillator.frequency.setValueAtTime(frequency, startAt)
  gain.gain.setValueAtTime(0.0001, startAt)
  gain.gain.exponentialRampToValueAtTime(1, startAt + 0.02)
  gain.gain.exponentialRampToValueAtTime(0.0001, stopAt)

  oscillator.connect(gain)
  gain.connect(destination)
  oscillator.start(startAt)
  oscillator.stop(stopAt + 0.02)
  oscillator.addEventListener(
    'ended',
    () => {
      oscillator.disconnect()
      gain.disconnect()
    },
    { once: true },
  )
}

export function useNotifications() {
  const context = useContext(NotificationsContext)

  if (!context) {
    throw new Error('useNotifications must be used within NotificationsProvider')
  }

  return context
}

function NotificationViewport({
  notifications,
  onDismiss,
}: {
  notifications: Notification[]
  onDismiss: (id: string) => void
}) {
  if (notifications.length === 0) {
    return null
  }

  return (
    <ol
      aria-live="polite"
      aria-relevant="additions removals"
      className="pointer-events-none fixed right-4 bottom-4 z-[60] flex w-[calc(100vw-2rem)] max-w-md flex-col gap-3 sm:right-6 sm:bottom-6"
    >
      {notifications.map((notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onDismiss={() => onDismiss(notification.id)}
        />
      ))}
    </ol>
  )
}

function NotificationItem({
  notification,
  onDismiss,
}: {
  notification: Notification
  onDismiss: () => void
}) {
  const variant = variantStyles[notification.variant]
  const Icon = variant.icon

  return (
    <li
      role={notification.variant === 'error' ? 'alert' : 'status'}
      className={cn(
        'pointer-events-auto grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3 rounded-lg border border-l-4 p-4 text-popover-foreground shadow-2xl ring-1',
        'animate-in slide-in-from-bottom-2 fade-in duration-200',
        variant.accentClassName,
        variant.containerClassName,
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md shadow-sm',
          variant.iconClassName,
        )}
      >
        <Icon className="size-4" />
      </span>
      <div className="min-w-0">
        <p className="text-sm font-semibold leading-5 text-foreground">{notification.title}</p>
        <p className="mt-1 text-sm leading-5 text-muted-foreground">{notification.description}</p>
      </div>
      <button
        type="button"
        aria-label="Zavřít notifikaci"
        className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        onClick={onDismiss}
      >
        <X className="size-4" />
      </button>
    </li>
  )
}
